import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

interface IProps {
  children: React.ReactNode;
  leftHandle: React.ReactNode;
  rightHandle: React.ReactNode;
  className?: string;
}

type Direction = "left" | "right";

const Root = styled.div`
  position: relative;
`;

const Content = styled.div`
  overflow-x: scroll;
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    background: transparent;
    appearance: none;
    width: 0;
    height: 0;
  }
`;

const Left = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  cursor: pointer;
`;

const Right = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  cursor: pointer;
`;

const useSlide = (children) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollX, setScrollX] = useState<number>(0);
  const [scrollEnd, setScrollEnd] = useState<boolean>(false);

  const computeScrollEnd = (ref) =>
    Math.floor(ref.scrollWidth - ref.scrollLeft) <= ref.offsetWidth;

  const slide = (direction: Direction) => {
    const refEl = contentRef.current;

    if (refEl) {
      refEl.scrollLeft += (direction === "left" ? -1 : 1) * refEl.offsetWidth;
      setScrollX(scrollX + refEl.offsetWidth);
      setScrollEnd(computeScrollEnd(refEl));
    }
  };

  const checkScroll = useCallback(() => {
    const refEl = contentRef.current;

    if (refEl) {
      setScrollX(refEl.scrollLeft);
      setScrollEnd(computeScrollEnd(refEl));
    }
  }, [contentRef]);

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [children, checkScroll]);

  const showLeft = scrollX !== 0;
  const showRight = !scrollEnd;

  return { contentRef, showLeft, showRight, slide, scrollCheck: checkScroll };
};

const UiKitUtilsSlidingStrip = React.forwardRef(
  (
    { children, leftHandle, rightHandle, className }: IProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const { contentRef, showLeft, showRight, slide, scrollCheck } =
      useSlide(children);

    return (
      <Root ref={ref}>
        <Content ref={contentRef} onScroll={scrollCheck} className={className}>
          {children}
        </Content>
        {showLeft && <Left onClick={() => slide("left")}>{leftHandle}</Left>}
        {showRight && (
          <Right onClick={() => slide("right")}>{rightHandle}</Right>
        )}
      </Root>
    );
  }
);

export default UiKitUtilsSlidingStrip;
